import React from 'react';
import SEO from '../components/SEO';

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 40px',
    fontFamily: '"Times New Roman", Times, serif',
    lineHeight: '1.6',
    color: '#333',
    backgroundColor: '#fff',
    paddingTop: '40px',
  },
  heading: {
    fontSize: '28px',
    fontWeight: '400',
    margin: '0 0 20px 0',
    letterSpacing: '1px',
    color: '#333',
    fontFamily: '"Times New Roman", Times, serif',
    textAlign: 'left',
  },
  subHeading: {
    fontSize: '18px',
    fontWeight: '400',
    margin: '40px 0 15px 0',
    color: '#333',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Times New Roman", Times, serif',
  },
  text: {
    fontSize: '16px',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '20px',
    fontFamily: '"Times New Roman", Times, serif',
  },
  philosophy: {
    border: '1px solid #ccc',
    padding: '25px',
    marginBottom: '30px',
    backgroundColor: '#fff',
  },
  philosophyTitle: {
    fontSize: '18px',
    fontWeight: '400',
    color: '#333',
    marginBottom: '15px',
    fontFamily: '"Times New Roman", Times, serif',
  },
  principle: {
    borderLeft: '3px solid #333',
    paddingLeft: '20px',
    marginBottom: '25px',
  },
  principleTitle: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#333',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
    fontFamily: '"Times New Roman", Times, serif',
  }
};

function InvestmentPhilosophy() {
  return (
    <div style={styles.container}>
      <SEO
        title="Investment Philosophy - Sapienta VC"
        description="Learn about Sapienta VC's investment philosophy, core principles, and approach to building long-term value with portfolio companies."
        keywords="investment philosophy, venture capital principles, long-term investing, sapienta vc approach"
        url="https://sapienta.vc/investment-philosophy"
      />

      <p style={styles.text}>
        At Sapienta Venture Capital, our investment philosophy is built on the foundation of backing
        ambitious projects with a long-term perspective. We believe that sustainable value creation
        comes from supporting exceptional founders who are building transformative companies in
        payment innovation, technology excellence, and AI-driven solutions.
      </p>

      <h2 style={styles.subHeading}>Core Investment Principles</h2>

      <div style={styles.principle}>
        <h3 style={styles.principleTitle}>Payment Innovation</h3>
        <p style={styles.text}>
          We focus on building ventures that revolutionize financial transactions and payment systems,
          ensuring seamless, secure, and scalable solutions that drive the future of digital commerce.
          Our investments target companies creating new paradigms in how money moves globally.
        </p>
      </div>

      <div style={styles.principle}>
        <h3 style={styles.principleTitle}>Technology Excellence</h3>
        <p style={styles.text}>
          As a founder-driven group, we prioritize cutting-edge technological advancement and long-term
          innovation over short-term gains, empowering ventures to lead in rapidly evolving tech landscapes.
          We seek companies building foundational infrastructure for the digital future.
        </p>
      </div>

      <div style={styles.principle}>
        <h3 style={styles.principleTitle}>AI-Driven Conviction</h3>
        <p style={styles.text}>
          We act with conviction on artificial intelligence and machine learning investments that are
          transforming industries – backing the intelligent solutions that shape tomorrow's world.
          Our focus is on practical AI applications that solve real business problems.
        </p>
      </div>

      <h2 style={styles.subHeading}>Investment Approach</h2>

      <div style={styles.philosophy}>
        <h3 style={styles.philosophyTitle}>Long-Term Partnership</h3>
        <p style={styles.text}>
          We believe in building enduring relationships with entrepreneurs. Our approach extends beyond
          capital provision to include strategic guidance, operational support, and access to our
          extensive network. We work closely with founders through multiple funding rounds and
          business cycles.
        </p>
      </div>

      <div style={styles.philosophy}>
        <h3 style={styles.philosophyTitle}>Sustainable Growth</h3>
        <p style={styles.text}>
          Rather than pursuing growth at any cost, we focus on sustainable business models with
          strong unit economics. We help our portfolio companies build defensible competitive
          advantages and viable paths to profitability while scaling efficiently.
        </p>
      </div>

      <div style={styles.philosophy}>
        <h3 style={styles.philosophyTitle}>Conviction-Based Investing</h3>
        <p style={styles.text}>
          We make concentrated bets on companies and themes we deeply understand. Our investment
          decisions are driven by thorough research, industry expertise, and conviction in the
          transformative potential of the technologies and markets we target.
        </p>
      </div>

      <h2 style={styles.subHeading}>Value Creation Strategy</h2>

      <p style={styles.text}>
        Our value creation strategy focuses on three key areas:
      </p>

      <p style={styles.text}>
        <strong>Strategic Capital:</strong> We provide not just funding, but strategic capital that
        comes with deep industry knowledge, network access, and operational expertise. Our investments
        are sized and structured to maximize impact for portfolio companies.
      </p>

      <p style={styles.text}>
        <strong>Hands-On Support:</strong> We offer hands-on guidance in critical areas including
        go-to-market strategy, product development, team building, and business development.
        Our partners work closely with management teams during pivotal growth phases.
      </p>

      <p style={styles.text}>
        <strong>Ecosystem Benefits:</strong> Portfolio companies gain access to our broader ecosystem,
        enabling cross-company collaboration, knowledge sharing, and potential business development
        opportunities. We facilitate connections that create mutual value.
      </p>

      <h2 style={styles.subHeading}>Market Perspective</h2>

      <p style={styles.text}>
        We believe we are in the early stages of a technological transformation driven by artificial
        intelligence, blockchain technology, and new payment infrastructures. Companies that can
        successfully navigate this transformation while building sustainable businesses will create
        significant long-term value.
      </p>

      <p style={styles.text}>
        Our investment philosophy recognizes that the most successful venture investments often require
        patience, conviction, and the ability to support companies through multiple market cycles.
        We are committed to being the kind of partner that entrepreneurs want by their side as they
        build category-defining companies.
      </p>

      <h2 style={styles.subHeading}>Risk Management</h2>

      <p style={styles.text}>
        While we embrace the inherent risks of early-stage investing, we employ disciplined risk
        management practices. This includes thorough due diligence, diversified portfolio construction,
        and ongoing monitoring of portfolio company performance. We balance ambitious growth targets
        with prudent financial management.
      </p>

      <h2 style={styles.subHeading}>Looking Forward</h2>

      <p style={styles.text}>
        As we continue to evolve our investment philosophy, we remain committed to our core mission
        of backing ambitious projects that can create lasting positive impact. We are excited about
        the opportunities ahead and look forward to partnering with the next generation of
        transformative technology companies.
      </p>
    </div>
  );
}

export default InvestmentPhilosophy;